import { z } from 'zod/v4';
import { InteractionType } from '../../interactions/index.js';
import { ToolCategory, ToolName, ToolPolicy, ToolWeight } from '../types.js';
export const AGENT_INTERACTION_TYPES = [
    InteractionType.Email,
    InteractionType.SMS,
    InteractionType.Letter,
];
export var InteractionRecipient;
(function (InteractionRecipient) {
    InteractionRecipient["Debtor"] = "DEBTOR";
    InteractionRecipient["Creditor"] = "CREDITOR";
})(InteractionRecipient || (InteractionRecipient = {}));
// TODO: When create_interaction supports explicit third-party recipients
// (representatives, lawyers, courts, employers, etc.), update the guidance below
// so the agent can reply to those senders directly instead of reporting out.
const inputSchema = z.object({
    recipient: z
        .enum(InteractionRecipient)
        .describe("Who the message is being sent to. Only DEBTOR (the person/company on the case) is supported by this tool. Use create_creditor_message for the case creditor. Do not use DEBTOR as a workaround for replying to a representative, lawyer, court, employer, or other third-party sender; if the reply should go to that sender and no matching recipient is available, call escalate_to_human. The body's language is derived from the recipient's preferred language at send time."),
    type: z
        .enum(AGENT_INTERACTION_TYPES)
        .describe('Channel to send through. Only EMAIL is currently supported; legacy SMS and LETTER values remain in the schema solely so stored proposals can still be inspected safely.'),
    subject: z
        .string()
        .max(255)
        .optional()
        .describe('Subject line. Required for EMAIL unless inReplyToInteractionId is set — replies ALWAYS use the original message\'s subject with a localized reply prefix ("Re:" / "Sv:"), and any subject provided here is ignored. Not used for SMS or LETTER.'),
    templateId: z
        .uuid()
        .optional()
        .describe('Template providing the surrounding layout. REQUIRED for LETTER — a letter generates a PDF and the template determines the header, footer, address block, signature, and other wrap around your body. Optional for EMAIL (informational, helps the caseworker review). Not used for SMS. Obtain via search_templates.'),
    body: z
        .string()
        .min(1)
        .max(20_000)
        .describe([
        'The message body. HTML for EMAIL and LETTER; plain text for SMS.',
        '',
        'DO NOT write a closing signature. The send pipeline appends the tenant\'s sign-off automatically (Med venlig hilsen / Best regards, collector name, contact details). Adding your own "Med venlig hilsen, {{collector.name}}" block at the end produces a duplicate signature. End the body with the last substantive sentence; the wrapper handles the rest. (The opening salutation — Kære {{debtor.name}} / Dear X — IS up to you.)',
        '',
        'RULE: NEVER inline a value that has a {{mention}}. Mentions resolve at send time against the live case state, so a name, address, amount, date, or url that changes between proposal and send is captured correctly. Inlined values go stale and bypass tenant-controlled formatting. A proposal that inlines mentionable values will be rejected at review.',
        '',
        'Call search_mentions to see what handles exist (categories: debtor, creditor, case, contact, collector, financials, accord, payment_plan, dates, user, court, tribunal, conditionals). The default answer for any name, address, amount, date, or url is ALWAYS the mention handle, used verbatim with braces. Only inline a literal value when no mention covers it (e.g. a specific clause you are quoting from a prior letter).',
        '',
        "Match the tone of prior outbound interactions on this case. Search templates first to align with the tenant's voice — they also show which mentions this tenant actually uses, and where they expect the body to end before the signature.",
        '',
        'Keep debtor-facing wording respectful, clear, and neutral. Do not expose internal workflow/update labels, monitoring stages, payment-plan minimums or eligibility thresholds, review procedures, agent constraints, or creditor mandates. State only the debtor-relevant outcome and next step.',
    ].join('\n')),
    attachments: z
        .array(z.object({
        fileId: z
            .uuid()
            .describe('Existing file to attach. Obtain via search_documents, get_interaction.attachments, or other tools that return file ids.'),
    }))
        .optional()
        .describe('Files to attach to the outgoing message. Only existing files on this case can be attached — the agent cannot upload new files. Common use: replying with a document the recipient asked about (e.g. the foundation invoice).'),
    inReplyToInteractionId: z
        .uuid()
        .optional()
        .describe('Required when the message responds to a specific prior interaction. Threading is how the caseworker review surface shows the conversation context; omit only for unsolicited outreach.'),
    reasoning: z
        .string()
        .max(500)
        .optional()
        .describe('Why you are proposing this interaction. Short rationale visible to the caseworker reviewing the proposal.'),
});
const outputSchema = z.object({
    interactionId: z.uuid(),
});
// Edited attachment fileIds are re-validated against the case at apply.
const editSchema = z.object({
    subject: z.string().max(255).optional(),
    body: z.string().min(1).max(20_000).optional(),
    attachments: z.array(z.object({ fileId: z.uuid() })).optional(),
});
export const createInteractionTool = {
    name: ToolName.CreateInteraction,
    category: ToolCategory.Interactions,
    weight: ToolWeight.Heavy,
    version: 1,
    defaultPolicy: ToolPolicy.RequireApproval,
    llmDescription: "Propose sending an EMAIL to the debtor on this case. Use create_creditor_message for the case creditor; third-party messaging is not available. HEAVY: calling this ends the run; a caseworker reviews and may edit before approving. Never answer a representative, lawyer, court, employer, or other third-party inbound message by emailing the debtor. If the response should go to the third-party sender, call escalate_to_human instead. Write the full body in the recipient's language with a respectful, clear, neutral tone. Do not disclose internal workflow/update labels, monitoring stages, payment-plan minimums or eligibility thresholds, review procedures, agent constraints, or creditor mandates; communicate only the debtor-relevant outcome and next step. Search templates first to align with the tenant's tone AND to learn which {{mention}} placeholders the tenant uses. CRITICAL: never inline a name, address, amount, date, or url that has a {{mention}} — they resolve at send time against live case state. A proposal that inlines mentionable values will be rejected.",
    inputSchema,
    outputSchema,
    editSchema,
    previewable: true,
    summarizeProposal: (input) => `Send ${input.type} to ${input.recipient}`,
};
//# sourceMappingURL=create-interaction.js.map